/**
 * AN-ACC Reassessment Service
 * Automates AN-ACC reassessment scheduling and triggers
 */

import LocalStorageService from '../storage/LocalStorageService';
import ANACCService from '../api/ANACCService';

class ANACCReassessmentService {
  constructor() {
    this.storageKey = 'anaccReassessments';
  }

  /**
   * Check if resident is eligible for reassessment
   */
  async checkReassessmentEligibility(residentId) {
    try {
      // Get current AN-ACC assessment
      const anaccResult = await ANACCService.getByResidentId(residentId);
      if (!anaccResult.success || !anaccResult.data) {
        return { 
          success: false, 
          eligible: false, 
          reason: 'No AN-ACC assessment found' 
        };
      }
      
      const assessment = anaccResult.data;
      const anaccClass = assessment.anaccClass;
      const lastAssessmentDate = new Date(assessment.assessmentDate || assessment.classificationDate);
      const today = new Date();
      
      // Determine reassessment period based on class
      // Classes 9-12: 6 months, Classes 2-8: 12 months
      let reassessmentPeriodMonths = 12;
      if (anaccClass >= 9 && anaccClass <= 12) {
        reassessmentPeriodMonths = 6;
      }
      
      // Calculate next reassessment date
      const nextReassessmentDate = new Date(lastAssessmentDate);
      nextReassessmentDate.setMonth(nextReassessmentDate.getMonth() + reassessmentPeriodMonths);
      
      // Check if eligible
      const daysUntilEligible = Math.ceil((nextReassessmentDate - today) / (1000 * 60 * 60 * 24));
      const eligible = daysUntilEligible <= 0;
      
      return {
        success: true,
        eligible,
        anaccClass,
        lastAssessmentDate: lastAssessmentDate.toISOString().split('T')[0],
        reassessmentPeriodMonths,
        nextReassessmentDate: nextReassessmentDate.toISOString().split('T')[0],
        daysUntilEligible: eligible ? 0 : daysUntilEligible,
        daysOverdue: eligible ? Math.abs(daysUntilEligible) : 0
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Check for hospitalization triggers
   */
  async checkHospitalizationTriggers(residentId, hospitalizationData) {
    try {
      const { admissionDate, dischargeDate, generalAnesthesia, daysInHospital } = hospitalizationData;
      
      // Check if hospitalization triggers reassessment
      // Trigger if: 5+ days OR 2+ days with general anesthesia
      const triggersReassessment = 
        daysInHospital >= 5 || 
        (daysInHospital >= 2 && generalAnesthesia);
      
      if (triggersReassessment) {
        // Create reassessment task
        const reassessment = {
          id: `reassess-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          residentId,
          triggerType: 'hospitalization',
          triggerDate: dischargeDate || new Date().toISOString().split('T')[0],
          hospitalizationData: {
            admissionDate,
            dischargeDate,
            daysInHospital,
            generalAnesthesia
          },
          status: 'pending',
          priority: 'high',
          dueDate: new Date().toISOString().split('T')[0], // Immediate
          createdAt: new Date().toISOString()
        };
        
        const created = LocalStorageService.addItem(this.storageKey, reassessment);
        return { 
          success: true, 
          triggersReassessment: true, 
          reassessment: created 
        };
      }
      
      return { success: true, triggersReassessment: false };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Get upcoming reassessments
   */
  async getUpcomingReassessments(facilityId = null, daysAhead = 30) {
    try {
      const all = LocalStorageService.getAll(this.storageKey);
      const today = new Date();
      const futureDate = new Date();
      futureDate.setDate(futureDate.getDate() + daysAhead);
      
      let filtered = all.filter(reassess => {
        const dueDate = new Date(reassess.dueDate || reassess.nextReassessmentDate);
        return dueDate >= today && dueDate <= futureDate;
      });
      
      if (facilityId) {
        // Filter by facility (would need resident data)
        // For now, return all
      }
      
      // Sort by due date
      filtered.sort((a, b) => {
        const dateA = new Date(a.dueDate || a.nextReassessmentDate);
        const dateB = new Date(b.dueDate || b.nextReassessmentDate);
        return dateA - dateB;
      });
      
      return { success: true, data: filtered };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Get overdue reassessments
   */
  async getOverdueReassessments(facilityId = null) {
    try {
      const all = LocalStorageService.getAll(this.storageKey);
      const today = new Date();
      
      let filtered = all.filter(reassess => {
        const dueDate = new Date(reassess.dueDate || reassess.nextReassessmentDate);
        return dueDate < today && reassess.status !== 'completed';
      });
      
      if (facilityId) {
        // Filter by facility
      }
      
      return { success: true, data: filtered };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Schedule reassessment reminders
   */
  async scheduleReminders(residentId) {
    try {
      const eligibility = await this.checkReassessmentEligibility(residentId);
      if (!eligibility.success) {
        return eligibility;
      }
      
      const { nextReassessmentDate, daysUntilEligible } = eligibility;
      
      // Create reminders at 30, 14, and 7 days before due
      const reminderDays = [30, 14, 7];
      const reminders = [];
      
      reminderDays.forEach(days => {
        if (daysUntilEligible <= days && daysUntilEligible > 0) {
          const reminderDate = new Date(nextReassessmentDate);
          reminderDate.setDate(reminderDate.getDate() - days);
          
          reminders.push({
            residentId,
            type: 'reassessment_reminder',
            reminderDate: reminderDate.toISOString().split('T')[0],
            daysBefore: days,
            sent: false,
            createdAt: new Date().toISOString()
          });
        }
      });
      
      return { success: true, data: reminders };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Get reassessment schedule for a resident
   */
  async getReassessmentSchedule(residentId) {
    try {
      const eligibility = await this.checkReassessmentEligibility(residentId);
      if (!eligibility.success) {
        return eligibility;
      }
      
      const anaccResult = await ANACCService.getByResidentId(residentId);
      const assessment = anaccResult.success ? anaccResult.data : null;
      
      return {
        success: true,
        data: {
          residentId,
          currentClass: eligibility.anaccClass,
          lastAssessmentDate: eligibility.lastAssessmentDate,
          reassessmentPeriodMonths: eligibility.reassessmentPeriodMonths,
          nextReassessmentDate: eligibility.nextReassessmentDate,
          daysUntilEligible: eligibility.daysUntilEligible,
          eligible: eligibility.eligible,
          overdue: eligibility.eligible && eligibility.daysUntilEligible < 0,
          assessment
        }
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
}

export default new ANACCReassessmentService();
